// Lógica pura do módulo de horas — sem React, sem antd, sem rede.
//
// Separado de `api.ts` porque o jest do CRA não carrega o antd v6 (exports
// map), então tudo que importa componente quebra a suíte ao ser carregado.
// Aqui não há import de UI: dá para testar de verdade.
import type { Dayjs } from 'dayjs';

/** Segunda-feira da semana da data.
 *
 *  `startOf('week')` do dayjs cai no DOMINGO no locale padrão. Usado direto,
 *  a grade mostraria domingo como primeiro dia e — pior — no domingo à noite
 *  a pessoa veria a semana que ainda vai começar, em vez da que acabou de
 *  trabalhar. Aqui domingo pertence à semana que termina nele.
 */
export const segundaDe = (d: Dayjs): Dayjs => d.subtract((d.day() + 6) % 7, 'day');

/** Chave da linha da grade: contrato OU centro de custo estrutural. */
export const chaveDestino = (a: { contrato_id: string | null; centro_custo_codigo: string | null }) =>
  a.contrato_id ? `c:${a.contrato_id}` : `e:${a.centro_custo_codigo}`;

export const destinoDaChave = (k: string) =>
  k.startsWith('c:')
    ? { contrato_id: k.slice(2), centro_custo_codigo: null }
    : { contrato_id: null, centro_custo_codigo: k.slice(2) };

/** Minutos → horas para exibir. A pessoa pensa em horas; o banco guarda minutos. */
export const minParaHoras = (m: number) => Math.round((m / 60) * 100) / 100;

/** Horas → minutos para gravar. Arredonda para o minuto: o banco é INTEGER e
 *  minuto fracionado viraria erro de CHECK no meio de um dia normal. */
export const horasParaMin = (h: number) => Math.round(h * 60);

/** O que a pessoa digitou na célula → minutos.
 *
 *  Nasceu de um erro real (Lucas, 21/08): o campo é em HORAS, mas nada na tela
 *  dizia isso. Ele digitou `60` querendo 60 minutos, virou 60 horas, e o
 *  servidor respondeu "minutos entre 1 e 1440" — uma mensagem que fala de
 *  minutos para quem acabou de digitar minutos. Exigir que a pessoa acerte a
 *  unidade é o tipo de atrito que faz ninguém anotar.
 *
 *  Formatos aceitos: `1,5` `1.5` `1:30` `1h30` `1h` `90m` `90min` `8`.
 *
 *  Número solto acima de 24 é lido como MINUTOS: não existe dia com mais de
 *  24 horas, então `60` só pode ser 60 minutos. A tela mostra o que entendeu
 *  ("60 → 1 h"), para a heurística nunca virar mágica silenciosa.
 *
 *  Devolve `{ minutos }` ou `{ erro }` — nunca adivinha no escuro.
 */
export function parseEntradaHoras(bruto: string): { minutos: number; interpretado?: string } | { erro: string } {
  const t = String(bruto ?? '').trim().toLowerCase().replace(/\s+/g, '');
  if (!t) return { minutos: 0 };

  const invalido = { erro: 'Não entendi. Use 1,5 para uma hora e meia, ou 1:30.' };

  // 1:30 · 1h30 · 1h
  const comp = t.match(/^(\d{1,2})(?::|h)(\d{1,2})?(?:min|m)?$/);
  if (comp) {
    const h = Number(comp[1]);
    const m = comp[2] ? Number(comp[2]) : 0;
    if (m > 59) return { erro: 'Os minutos vão até 59.' };
    const total = h * 60 + m;
    if (total > 1440) return { erro: 'Um dia tem no máximo 24 horas.' };
    return { minutos: total };
  }

  // 90m · 90min
  const soMin = t.match(/^(\d+(?:[.,]\d+)?)(?:min|m)$/);
  if (soMin) {
    const m = Math.round(Number(soMin[1].replace(',', '.')));
    if (m > 1440) return { erro: 'Um dia tem no máximo 24 horas.' };
    return { minutos: m };
  }

  // Número solto: horas, salvo quando é grande demais para ser hora.
  const soNum = t.match(/^(\d+(?:[.,]\d+)?)h?$/);
  if (!soNum) return invalido;
  const n = Number(soNum[1].replace(',', '.'));
  if (!Number.isFinite(n)) return invalido;
  if (n < 0) return invalido;

  const explicitamenteHora = t.endsWith('h');
  if (n > 24 && explicitamenteHora) return { erro: 'Um dia tem no máximo 24 horas.' };
  if (n > 1440) return { erro: 'Um dia tem no máximo 24 horas.' };

  // Acima de 24 não pode ser hora — só pode ser minuto.
  if (!explicitamenteHora && n > 24) {
    const m = Math.round(n);
    return { minutos: m, interpretado: `${bruto.trim()} → ${formatarHoras(m)}` };
  }

  const m = Math.round(n * 60);
  // Faixa duvidosa: "20" tanto pode ser 20 h (plantão) quanto 20 min. Vale
  // como hora — mas nunca em silêncio, senão vira 20 h no custo do contrato
  // sem ninguém perceber. Até 12, ninguém se confunde.
  if (!explicitamenteHora && n > 12) {
    return { minutos: m, interpretado: `${bruto.trim()} → ${formatarHoras(m)}` };
  }
  return { minutos: m };
}

/** Minutos → texto curto para mostrar de volta ("1 h", "1h30", "45 min"). */
export function formatarHoras(min: number): string {
  if (min <= 0) return '0 h';
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h}h${String(m).padStart(2, '0')}`;
}

/** Minutos → horas em decimal, à brasileira: "7,5 h", "7,33 h", "0,75 h". */
export function formatarHorasDecimal(min: number): string {
  return `${String(minParaHoras(Math.max(0, min))).replace('.', ',')} h`;
}

/** Como mostrar um TOTAL de horas (pedido do Lucas, 30/09/2026): primeiro com
 *  minutos ("7h30"), e embaixo, menor e cinza, em decimal ("7,5 h").
 *
 *  O decimal só existe quando diz algo que a primeira linha não disse: hora
 *  cheia ("8 h") não ganha um segundo "8 h" embaixo. Quem desenha decide se
 *  reserva o espaço da segunda linha para as colunas vizinhas não desalinharem
 *  (componente `TotalHoras`). */
export function partesDoTotal(min: number): { principal: string; decimal: string | null } {
  const m = Math.max(0, Math.round(Number(min) || 0));
  return { principal: formatarHoras(m), decimal: m % 60 !== 0 ? formatarHorasDecimal(m) : null };
}

/** Algum destes totais tem minuto quebrado? Serve para reservar a segunda
 *  linha em TODAS as colunas de uma fileira só quando ela vai aparecer. */
export const algumTotalQuebrado = (totais: number[]): boolean =>
  totais.some(t => partesDoTotal(t).decimal !== null);

/** Estado do dia de hoje para o indicador do atalho no cabeçalho.
 *
 *  `'pendente'` (laranja) = nada anotado hoje · `null` = nada a cobrar (há hora
 *  lançada, ou é ausência).
 *
 *  Não existe mais "fechar o dia" (01/10/2026): o estado azul "tem horas mas
 *  não fechou" saiu junto com o botão — ficaria aceso o dia inteiro para todo
 *  mundo, e aviso que nunca apaga vira ruído que se aprende a ignorar. Quanto
 *  falta para completar a carga é assunto do painel e da cobrança, não do ponto.
 */
export function estadoDoDia(
  status: string | null | undefined,
  minutosTotal: number,
): 'pendente' | null {
  if (status === 'ausencia') return null;
  return minutosTotal > 0 ? null : 'pendente';
}

// ── Onde a pessoa trabalhou no dia (30/09/2026) ──────────────────────────
//
// Duas caixas independentes no DIA: home office/remoto e campo. Nenhuma
// marcada = escritório. A régua de "pode marcar" é a mesma do servidor: até
// hoje (em SP — o `hoje` vem do servidor) e fora de dia de ausência.

export type MarcaLocal = 'remoto' | 'campo';

export function podeMarcarLocal(
  data: string,
  hoje: string | null | undefined,
  status: string | null | undefined,
): boolean {
  if (!hoje) return false;          // sem o relógio do servidor, não adivinha
  return data <= hoje && status !== 'ausencia';
}

/** A caixa responde na hora: aplica a marca na lista de dias que a tela já
 *  tem, criando o dia (aberto, sem horas) quando ele ainda não existe. Mexe só
 *  na marca pedida — a outra caixa fica como estava. Não altera a lista
 *  recebida. */
export function aplicarMarcaLocal<D extends { data: string; remoto?: boolean; campo?: boolean }>(
  dias: D[],
  data: string,
  marca: MarcaLocal,
  valor: boolean,
): D[] {
  if (dias.some(d => d.data === data)) {
    return dias.map(d => (d.data === data ? { ...d, [marca]: valor } : d));
  }
  const novo = {
    data, status: 'aberto', tipo_ausencia: null, minutos_total: 0, observacao: null,
    remoto: false, campo: false, [marca]: valor,
  } as unknown as D;
  return [...dias, novo].sort((a, b) => a.data.localeCompare(b.data));
}

// ── Horário de início e fim (opcional, desde 23/09/2026) ─────────────────
//
// O relógio é SEMPRE o de São Paulo: o servidor monta o instante com
// `AT TIME ZONE 'America/Sao_Paulo'` e devolve 'HH:MM' já nesse fuso. A tela
// só lida com minutos desde a meia-noite (0–1440) — nada de Date do navegador
// no meio, para uma máquina com o fuso errado não deslocar o bloco.

/** O que a pessoa digitou num campo de horário → minutos desde 00:00.
 *
 *  Aceita como se fala: `8` `8h` `08` `8:30` `8h30` `0830` `830` `24:00`.
 *  `24:00` só existe como FIM ("trabalhei até meia-noite"); quem chama decide
 *  se aceita via `permitir24`. Vazio devolve `{ vazio: true }` — horário é
 *  opcional, e "não disse" é diferente de "disse errado".
 */
export function parseHorario(
  bruto: string, { permitir24 = false }: { permitir24?: boolean } = {},
): { minutos: number } | { vazio: true } | { erro: string } {
  const t = String(bruto ?? '').trim().toLowerCase().replace(/\s+/g, '');
  if (!t) return { vazio: true };
  let h: number; let m: number;
  const sep = t.match(/^(\d{1,2})(?::|h|\.)(\d{2})?h?$/);
  const junto = t.match(/^(\d{3,4})$/);
  const soHora = t.match(/^(\d{1,2})h?$/);
  if (sep) { h = Number(sep[1]); m = sep[2] ? Number(sep[2]) : 0; }
  else if (junto) { const s = junto[1].padStart(4, '0'); h = Number(s.slice(0, 2)); m = Number(s.slice(2)); }
  else if (soHora) { h = Number(soHora[1]); m = 0; }
  else return { erro: 'Horário não entendido. Escreva 8:30 ou 830.' };
  if (m > 59) return { erro: 'Os minutos vão até 59.' };
  if (h === 24 && m === 0) {
    return permitir24 ? { minutos: 1440 } : { erro: 'O dia começa às 00:00 — 24:00 só vale como fim.' };
  }
  if (h > 23) return { erro: 'As horas vão de 0 a 23.' };
  return { minutos: h * 60 + m };
}

/** Minutos desde 00:00 → 'HH:MM' (1440 vira '24:00'). */
export function formatarHorario(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** 'HH:MM' (como o servidor devolve) → minutos. Texto estranho vira null. */
export function horarioParaMin(hhmm: string | null | undefined): number | null {
  if (!hhmm) return null;
  const r = String(hhmm).match(/^(\d{2}):(\d{2})/);
  if (!r) return null;
  return Number(r[1]) * 60 + Number(r[2]);
}

/** Duração entre início e fim, no mesmo dia. Fim antes do início é erro, não
 *  "virou a noite": atravessar a meia-noite são DOIS lançamentos, um por dia —
 *  é o dia que a cobrança e o fechamento enxergam. */
export function duracaoEntre(inicioMin: number, fimMin: number): { minutos: number } | { erro: string } {
  if (fimMin <= inicioMin) {
    return { erro: 'O fim tem que ser depois do início. Passou da meia-noite? Lance o resto no dia seguinte.' };
  }
  return { minutos: fimMin - inicioMin };
}

/** Arredonda para a grade do calendário (15 min por padrão). */
export const encaixar = (min: number, passo = 15) =>
  Math.max(0, Math.min(1440, Math.round(min / passo) * passo));

/** Bloco com horário, do jeito que o calendário precisa. */
export type BlocoDia = { id: string; inicio: number; fim: number };

/** Distribui os blocos de UM dia em colunas lado a lado quando se encostam.
 *
 *  O servidor recusa sobreposição, mas a tela não pode depender disso para
 *  não desenhar um bloco em cima do outro: o que veio de antes da regra (ou
 *  de outra fonte) tem que continuar legível e clicável. Cada grupo de blocos
 *  que se tocam divide a largura entre si.
 */
export function layoutDia<T extends BlocoDia>(blocos: T[]): Array<T & { coluna: number; colunas: number }> {
  const ord = blocos.slice().sort((a, b) => a.inicio - b.inicio || b.fim - a.fim);
  const saida: Array<T & { coluna: number; colunas: number }> = [];
  let grupo: Array<T & { coluna: number; colunas: number }> = [];
  let fimGrupo = -1;
  const fecharGrupo = () => {
    const n = grupo.reduce((mx, b) => Math.max(mx, b.coluna + 1), 1);
    grupo.forEach(b => { b.colunas = n; });
    saida.push(...grupo);
    grupo = [];
  };
  for (const b of ord) {
    if (grupo.length && b.inicio >= fimGrupo) { fecharGrupo(); fimGrupo = -1; }
    const ocupadas = new Set(grupo.filter(g => g.fim > b.inicio).map(g => g.coluna));
    let c = 0;
    while (ocupadas.has(c)) c++;
    grupo.push({ ...b, coluna: c, colunas: 1 });
    fimGrupo = Math.max(fimGrupo, b.fim);
  }
  if (grupo.length) fecharGrupo();
  return saida;
}

/** Tempo decorrido do cronômetro, em minutos inteiros.
 *
 *  O relógio do computador da pessoa pode estar errado (minutos, às vezes
 *  horas). Por isso a conta usa o `agora` do SERVIDOR, capturado junto com o
 *  cronômetro, e soma só o tanto que o relógio local andou desde então:
 *  diferença de relógio local contra ele mesmo é confiável, contra o
 *  servidor não é.
 */
export function decorrido(
  inicioIso: string, agoraServidorIso: string, recebidoEmLocalMs: number, agoraLocalMs: number,
): number {
  const ini = Date.parse(inicioIso);
  const srv = Date.parse(agoraServidorIso);
  if (!Number.isFinite(ini) || !Number.isFinite(srv)) return 0;
  const agora = srv + Math.max(0, agoraLocalMs - recebidoEmLocalMs);
  return Math.max(0, Math.floor((agora - ini) / 60000));
}

/** 'YYYY-MM-DD' + n dias, sem passar pelo fuso da máquina. */
export function somarDias(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
}

const DIAS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
/** Dia da semana em português de uma data 'YYYY-MM-DD'.
 *  O dayjs do projeto não carrega o locale pt: `format('ddd')` escrevia "Wed"
 *  no atalho do cabeçalho. */
export function diaDaSemana(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return DIAS_PT[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}

/** "Agora" em São Paulo — dia e minuto do dia — a partir do relógio do
 *  SERVIDOR (`hoje` + `agora_minuto` do GET /semana) mais o quanto o relógio
 *  local andou desde que a resposta chegou. Nunca o relógio do computador
 *  sozinho: fora de SP, ou com o Windows no fuso errado, a linha do "agora"
 *  e o fim sugerido sairiam uma hora deslocados. */
export function agoraSP(
  hojeServidor: string, minutoServidor: number, recebidoEmLocalMs: number, agoraLocalMs: number,
): { data: string; minuto: number } {
  const total = minutoServidor + Math.max(0, Math.floor((agoraLocalMs - recebidoEmLocalMs) / 60000));
  return { data: somarDias(hojeServidor, Math.floor(total / 1440)), minuto: total % 1440 };
}

// Cores do calendário: fundo claro + borda forte, uma por destino. A cor sai
// da CHAVE do destino (e não da ordem na tela), para o mesmo contrato ter a
// mesma cor em toda semana.
const PALETA: Array<[string, string]> = [
  ['#E7ECFD', '#0930D0'], ['#E3F5EA', '#1E8E4E'], ['#FFF0DE', '#C8620A'], ['#F2E8FD', '#7B3FC4'],
  ['#E1F4F6', '#0E7F8E'], ['#FDE7EC', '#C23556'], ['#EFF4DD', '#5F7F0E'], ['#ECEEF4', '#4A5670'],
];
export function corDoDestino(chave: string): { fundo: string; borda: string } {
  let h = 0;
  for (let i = 0; i < chave.length; i++) h = (h * 31 + chave.charCodeAt(i)) >>> 0;
  const [fundo, borda] = PALETA[h % PALETA.length];
  return { fundo, borda };
}

/** Intervalo de um arraste no calendário, em minutos, encaixado de 15 em 15
 *  e com pelo menos 15 min — arrastar para cima ou para baixo dá no mesmo. */
export function faixaDoArraste(m0: number, m1: number): [number, number] {
  const a = Math.max(0, Math.floor(Math.min(m0, m1) / 15) * 15);
  const b = Math.min(1440, Math.max(a + 15, Math.ceil(Math.max(m0, m1) / 15) * 15));
  return [a, b];
}

/** Segunda-feira da semana de uma data 'YYYY-MM-DD', sem passar pelo fuso da
 *  máquina (a data vem do servidor, em SP). */
export function segundaDeIso(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return somarDias(iso, -((dow + 6) % 7));
}

/** Hora contada duas vezes? — a mesma régua no atalho, na grade, na janela e
 *  no Parar do cronômetro.
 *
 *  Na troca de hábito, o erro provável é o cronômetro (ou o calendário)
 *  marcar a manhã e a pessoa, à tarde, anotar "8 h" como sempre fez. Não
 *  recusa — pode ser verdade —, pergunta com os números:
 *   · lançando SÓ A QUANTIDADE num dia que já tem horário (lançado ou
 *     cronômetro correndo): "são além delas?";
 *   · lançando COM HORÁRIO (ou parando o cronômetro) num dia que já tem
 *     quantidade sem horário, se o dia passar de horas_dia + 2 h;
 *   · qualquer caso em que o dia passe de horas_dia + 2 h.
 *  Devolve a frase da pergunta, ou null se não há o que perguntar.
 */
export function perguntaDeSoma(p: {
  data: string;
  novos: number;
  novoComHorario: boolean;
  doDia: Array<{ minutos: number; comHorario: boolean; faixa?: string | null }>;
  cronometroNoDia: number;
  horasDia: number;
}): string | null {
  const dm = `${p.data.slice(8, 10)}/${p.data.slice(5, 7)}`;
  const comHora = p.doDia.filter(x => x.comHorario);
  const semHora = p.doDia.filter(x => !x.comHorario);
  const minComHora = comHora.reduce((s, x) => s + x.minutos, 0) + p.cronometroNoDia;
  const minSemHora = semHora.reduce((s, x) => s + x.minutos, 0);
  const total = minComHora + minSemHora + p.novos;
  const limite = p.horasDia * 60 + 120;
  if (!p.novoComHorario && minComHora > 0) {
    const faixas = comHora.map(x => x.faixa).filter(Boolean);
    if (p.cronometroNoDia > 0) faixas.push('cronômetro ligado');
    return `Em ${dm} você já tem ${formatarHoras(minComHora)} com horário (${faixas.join(', ')}). Estas ${formatarHoras(p.novos)} são além delas?`;
  }
  if (p.novoComHorario && minSemHora > 0 && total > limite) {
    return `Em ${dm} já há ${formatarHoras(minSemHora)} lançadas sem horário. Com estas ${formatarHoras(p.novos)}, o dia fica com ${formatarHoras(total)}. Não são as mesmas horas?`;
  }
  if (total > limite) {
    return `Com estas, o dia ${dm} fica com ${formatarHoras(total)} — mais que as ${p.horasDia} h do seu dia. Está certo?`;
  }
  return null;
}

/** Minutos → '0:42' / '3:05' para o relógio do cronômetro. */
export function formatarRelogio(min: number): string {
  const h = Math.floor(min / 60);
  return `${h}:${String(min % 60).padStart(2, '0')}`;
}

export const TIPOS_AUSENCIA = [
  { value: 'ferias', label: 'Férias' },
  { value: 'atestado', label: 'Atestado' },
  { value: 'folga', label: 'Folga' },
  { value: 'feriado', label: 'Feriado' },
  { value: 'licenca', label: 'Licença' },
];
